import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Row, Col, Alert, Spinner, Badge } from "reactstrap";
import { toast } from "react-toastify";
import { importLeadsApi, downloadReferenceTemplateApi } from "../../apiServices/genericLeadServices";
import { getReassignUserListApi } from "../../apiServices/leadAssignment.services";
import useProjects from "../../Hooks/useProjects";
import useStages from "../../Hooks/useStages";

const LeadImportModal = ({ isOpen, toggle, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [defaultProject, setDefaultProject] = useState("");
  const [defaultStage, setDefaultStage] = useState("");
  const [defaultAssignedTo, setDefaultAssignedTo] = useState("");
  const [autoAssign, setAutoAssign] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [usersList, setUsersList] = useState([]);

  const { projects = [] } = useProjects({ limit: 1000 });
  const { stages = [] } = useStages({ limit: 1000 });

  const loadUsersList = async () => {
    try {
      const response = await getReassignUserListApi();
      const rawTree = response?.data || [];
      const flatten = (nodes = [], level = 0) => {
        let acc = [];
        nodes.forEach((n) => {
          acc.push({
            _id: n._id,
            name: `${"— ".repeat(level)}${n.firstName || ""} ${n.lastName || ""}`.trim(),
            email: n.email,
          });
          if (n.reportees && n.reportees.length > 0) {
            acc = acc.concat(flatten(n.reportees, level + 1));
          }
        });
        return acc;
      };
      setUsersList(flatten(rawTree));
    } catch (err) {
      console.error("Failed to load users list for import:", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadUsersList();
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];
      const ext = selectedFile.name.split(".").pop().toLowerCase();
      if (!["xlsx", "xls", "csv"].includes(ext) && !validTypes.includes(selectedFile.type)) {
        return toast.error("Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.");
      }
      setFile(selectedFile);
      setImportResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const blob = await downloadReferenceTemplateApi();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "generic_leads_data_entry_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sample template downloaded successfully!");
    } catch (err) {
      toast.error(err?.message || "Failed to download reference template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      return toast.warning("Please select an Excel or CSV file to import.");
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      if (defaultProject) formData.append("projectId", defaultProject);
      if (defaultStage) formData.append("stage", defaultStage);
      if (defaultAssignedTo) formData.append("assignedTo", defaultAssignedTo);
      formData.append("autoAssign", autoAssign);

      const response = await importLeadsApi(formData);
      const resultData = response?.data || {};

      setImportResult(resultData);
      toast.success(`Import complete! ${resultData.successCount || 0} leads created.`);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error(err?.message || "Failed to import leads file.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setDefaultProject("");
    setDefaultStage("");
    setDefaultAssignedTo("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        <i className="mdi mdi-file-excel-outline text-success me-2 fs-4" />
        Import Leads (Excel / CSV)
      </ModalHeader>

      <ModalBody className="px-4 py-3">
        {/* DOWNLOAD TEMPLATE ALERT */}
        <Alert color="info" className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <strong className="d-block mb-1">
              <i className="mdi mdi-information-outline me-1" />
              Need help formatting your sheet?
            </strong>
            <small className="text-muted">
              Download our sample template with pre-filled instruction tabs & valid Project / Stage reference lists.
            </small>
          </div>
          <Button
            color="primary"
            size="sm"
            className="fw-semibold px-3"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
          >
            {downloadingTemplate ? (
              <>
                <Spinner size="sm" className="me-1" /> Downloading...
              </>
            ) : (
              <>
                <i className="mdi mdi-download me-1" /> Download Sample Template
              </>
            )}
          </Button>
        </Alert>

        {!importResult ? (
          <form onSubmit={handleImportSubmit}>
            {/* FILE SELECTION DROPZONE */}
            <div className="mb-3">
              <Label className="fw-semibold">Select Excel / CSV File <span className="text-danger">*</span></Label>
              <div
                className="border border-2 border-dashed rounded p-4 text-center bg-light position-relative"
                style={{ borderColor: file ? "#2ab57d" : "#ced4da" }}
              >
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                  }}
                />
                {file ? (
                  <div>
                    <i className="mdi mdi-file-check-outline text-success" style={{ fontSize: "40px" }} />
                    <h6 className="mt-2 mb-1 fw-bold text-dark">{file.name}</h6>
                    <span className="badge bg-soft-success text-success fs-7">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                    <div className="mt-2">
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                      >
                        <i className="mdi mdi-trash-can-outline me-1" /> Change File
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <i className="mdi mdi-cloud-upload-outline text-primary" style={{ fontSize: "42px" }} />
                    <h6 className="mt-2 mb-1 text-secondary">Drag & Drop file here or Click to Browse</h6>
                    <small className="text-muted">Supports .xlsx, .xls, and .csv formats</small>
                  </div>
                )}
              </div>
            </div>

            {/* DEFAULT FALLBACK OPTIONS */}
            <Row className="g-3 mb-3">
              <Col md="4">
                <Label className="fw-semibold small">Default Project (Optional)</Label>
                <Input
                  type="select"
                  value={defaultProject}
                  onChange={(e) => setDefaultProject(e.target.value)}
                >
                  <option value="">-- From Sheet --</option>
                  {projects.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.projectName}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md="4">
                <Label className="fw-semibold small">Default Stage (Optional)</Label>
                <Input
                  type="select"
                  value={defaultStage}
                  onChange={(e) => setDefaultStage(e.target.value)}
                >
                  <option value="">-- From Sheet --</option>
                  {stages.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md="4">
                <Label className="fw-semibold small">Default Assigned To (Optional)</Label>
                <Input
                  type="select"
                  value={defaultAssignedTo}
                  onChange={(e) => setDefaultAssignedTo(e.target.value)}
                >
                  <option value="">-- From Sheet / Rule --</option>
                  {usersList.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name} {u.email ? `(${u.email})` : ""}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            <div className="d-flex align-items-center gap-3 mb-3 p-2 rounded bg-light border">
              <div
                onClick={() => setAutoAssign((prev) => !prev)}
                style={{
                  width: "44px",
                  height: "22px",
                  backgroundColor: autoAssign ? "#2ab57d" : "#ced4da",
                  borderRadius: "11px",
                  padding: "2px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: autoAssign ? "flex-end" : "flex-start",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: "18px",
                    height: "18px",
                    backgroundColor: "#ffffff",
                    borderRadius: "50%",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    transition: "all 0.2s ease-in-out",
                  }}
                />
              </div>
              <Label
                className="mb-0 fw-semibold text-dark small"
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => setAutoAssign((prev) => !prev)}
              >
                Apply Project Auto-Assignment Rules if Assigned To is left blank in Sheet
              </Label>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button color="light" onClick={handleClose} type="button">
                Cancel
              </Button>
              <Button color="success" type="submit" disabled={loading || !file} className="px-4">
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Importing...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-upload me-1" /> Start Import
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          /* IMPORT SUMMARY REPORT VIEW */
          <div>
            <div className="text-center py-3">
              <i className="mdi mdi-check-circle-outline text-success" style={{ fontSize: "56px" }} />
              <h4 className="fw-bold text-dark mt-2">Import Process Finished!</h4>
              <p className="text-muted">Here is the summary of your file import:</p>
            </div>

            <Row className="g-3 mb-4 text-center">
              <Col md="4">
                <div className="p-3 bg-light rounded border">
                  <span className="text-muted small d-block">Total Rows</span>
                  <h3 className="mb-0 fw-bold text-primary">{importResult.totalRows || 0}</h3>
                </div>
              </Col>
              <Col md="4">
                <div className="p-3 bg-soft-success rounded border border-success">
                  <span className="text-success small d-block">Successfully Created</span>
                  <h3 className="mb-0 fw-bold text-success">{importResult.successCount || 0}</h3>
                </div>
              </Col>
              <Col md="4">
                <div className="p-3 bg-soft-danger rounded border border-danger">
                  <span className="text-danger small d-block">Failed / Skipped</span>
                  <h3 className="mb-0 fw-bold text-danger">{importResult.failureCount || 0}</h3>
                </div>
              </Col>
            </Row>

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="mb-3">
                <h6 className="fw-bold text-danger mb-2">
                  <i className="mdi mdi-alert-circle-outline me-1" />
                  Row Error Details ({importResult.errors.length}):
                </h6>
                <div
                  className="bg-light p-3 rounded border"
                  style={{ maxHeight: "180px", overflowY: "auto", fontSize: "13px" }}
                >
                  {importResult.errors.map((errItem, idx) => (
                    <div key={idx} className="text-danger mb-1 border-bottom pb-1">
                      <strong>Row {errItem.row}:</strong> {errItem.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                color="secondary"
                outline
                onClick={() => {
                  setImportResult(null);
                  setFile(null);
                }}
              >
                Import Another File
              </Button>
              <Button color="primary" onClick={handleClose}>
                Done
              </Button>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default LeadImportModal;
